import { ref, Ref, watch } from 'vue'
import identityManager, { LoggedInUser } from './IdentityManager'
import { IArmador } from '../../services/api-services/ArmadoresServices'
import dataStorageManager, { BucketDefaultIDs } from '../data/DataStorageManager'
import { IProveedor } from '../../services/api-services/ProveedoresServices'
import { IRequest, RequestObjectType, RequestStatus, isApproved } from '../../services/api-services/RequestsServices'
import { IEmbarcacion } from '../../services/api-services/EmbarcacionesServices'
import { IPermisoControl, ActionsPermissions, SystemModules } from '../../services/api-services/RepresentantesServices'
import { resolveEmbarcacionAccess } from '../../services/utils/PermisosResolver'
import { normalizeRut } from '../../services/apiUtils'

export enum UserRoles {
  GENERICO = 8,
  ESPORADICO = 7,
  REPRESENTANTE = 6,
  INTERINO = 5,
  ADMIN = 4,
  SUPERVISOR = 3,
  OPERADOR = 2,
  GESTOR = 1,
  EXTERNO = 0,
}

export enum ExternalSpec {
  TRIPULANTE = 12,
  ARMADOR = 11,
  PROVEEDOR = 10,
}

export enum ArmadorType {
  MUELLE_ESTE = 110,
  ESTE = 110,
  MUELLE_WESTE = 111,
  WESTE = 111,
  JURIDICA = 112, // Persona jurídica — usado para filtrar vistas de representantes
  DIRECTO = 113, // Armador directo (cualquier tipo_persona) O representante de jurídica
}

export interface UserRole {
  role: UserRoles
  externalSpec?: ExternalSpec // Solo si el rol es EXTERNO
  armadorType?: ArmadorType // Solo si el rol es EXTERNO y la especificación es ARMADOR
  armadorIsJuridica?: boolean // Solo si externalSpec es ARMADOR
  isRepresentante?: boolean // true cuando un REPRESENTANTE opera como armador
}

// Manejador de capacidades de roles
// Se encarga de definir y gestionar las capacidades y permisos asociados a cada rol de usuario en el sistema.
// Esto incluye qué acciones puede realizar cada rol, qué datos puede ver y modificar, y cualquier restricción específica.
class RoleCapacitiesManager {
  private loggedUser: LoggedInUser | null = null
  private userRole: UserRoles | null = null // Cambiado a number para coincidir con UserRoles
  private userExternalSpec: ExternalSpec | null = null
  private userArmadorType: ArmadorType | null = null
  private userArmadorIsJuridica: boolean = false
  private roleDetails: UserRole | null = null
  private subscribers: Set<(data: UserRole | null) => void> = new Set()
  private refUserRole: Ref<UserRole | null> = ref(null)
  private armadorUpdateVersion: number = 0 // version counter para cancelar operaciones async obsoletas

  public loadPromise: Promise<boolean> | null = null
  private promiseActivator: ((resp: boolean) => void) | null = null

  constructor() {
    this.loadPromise = new Promise<boolean>((resolve) => {
      this.promiseActivator = resolve
    })
  }

  startup() {
    dataStorageManager.subscribeBucket<IArmador>(BucketDefaultIDs.ARMADORES, (data) => {})
    dataStorageManager.subscribeBucket<IProveedor>(BucketDefaultIDs.PROVEEDORES, (data) => {})

    this.detectUserLogged()

    watch(identityManager.getRefActiveArmadorId(), (newId) => {
      this.updateActiveArmador(newId)
    })
  }

  private async detectUserLogged() {
    await identityManager.loadPromise
    this.updateLoggedUser(identityManager.getUser())
    identityManager.subscribe(this.updateLoggedUser.bind(this))
  }

  private async updateLoggedUser(user: LoggedInUser | null) {
    console.log(`[RoleCapacitiesManager] updateLoggedUser rol=${user?.rol} id=${user?.id_usuario}`)
    this.loggedUser = user

    if (user) {
      this.userRole = user.rol as UserRoles // Asumiendo que user.rol es un número que coincide con UserRoles
      if (this.userRole === UserRoles.EXTERNO) {
        const userRutNorm = normalizeRut(user.rut)
        const searchedArmador = await dataStorageManager.getAndSearchDataInBucket(
          BucketDefaultIDs.ARMADORES,
          (armador: IArmador) =>
            normalizeRut(armador.rut_representante) === userRutNorm ||
            (armador.rut_empresa ? normalizeRut(armador.rut_empresa) === userRutNorm : false),
        )
        if (searchedArmador) {
          this.userExternalSpec = ExternalSpec.ARMADOR
          this.userArmadorType = ArmadorType.ESTE
          this.userArmadorIsJuridica = searchedArmador.tipo_persona === 'juridica'
          await this.findArmadorMuelle()
        } else {
          const searchedProveedor = await dataStorageManager.getAndSearchDataInBucket(
            BucketDefaultIDs.PROVEEDORES,
            (proveedor: IProveedor) =>
              normalizeRut(proveedor.rut_representante) === userRutNorm ||
              (proveedor.rut_empresa ? normalizeRut(proveedor.rut_empresa) === userRutNorm : false),
          )
          if (searchedProveedor) {
            this.userExternalSpec = ExternalSpec.PROVEEDOR
          } else {
            this.userExternalSpec = ExternalSpec.TRIPULANTE
          }
        }
      } else {
        this.userExternalSpec = null
        this.userArmadorType = null
      }
      this.roleDetails = {
        role: this.userRole,
        externalSpec: this.userExternalSpec || undefined,
        armadorType: this.userArmadorType || undefined,
        armadorIsJuridica: this.userArmadorIsJuridica || undefined,
      }
    } else {
      this.userRole = null
      this.userExternalSpec = null
      this.userArmadorType = null
      this.userArmadorIsJuridica = false
      this.roleDetails = null
    }
    this.refUserRole.value = this.roleDetails

    this.promiseActivator!(this.roleDetails !== null)

    this.notifySubscribers()
  }

  public subscribe(callback: (data: UserRole | null) => void) {
    this.subscribers.add(callback)
    if (this.roleDetails) callback(this.roleDetails)
  }

  private notifySubscribers() {
    this.refUserRole.value = this.roleDetails // Para subscriptores pasivos
    this.subscribers.forEach((callback) => callback(this.roleDetails)) // Para subscriptores reactivos
  }

  public getRoleDetails(): UserRole | null {
    return this.roleDetails
  }

  public getRefUserRole(): Ref<UserRole | null> {
    this.refUserRole.value = this.roleDetails
    return this.refUserRole
  }

  private async updateActiveArmador(armadorId: number | null) {
    const version = ++this.armadorUpdateVersion
    console.log(`[RoleCapacitiesManager] updateActiveArmador armadorId=${armadorId} version=${version}`)
    if (armadorId !== null) {
      // Acting as a represented armador — always expose as EXTERNO+ARMADOR so sidebar checks pass
      this.userExternalSpec = ExternalSpec.ARMADOR
      this.userArmadorType = ArmadorType.ESTE // default, refined below
      // Detect tipo_persona for the armador being represented
      const armadorEntry = await dataStorageManager.getAndSearchDataInBucket<IArmador>(
        BucketDefaultIDs.ARMADORES,
        (a: IArmador) => a.id === armadorId,
      )
      if (version !== this.armadorUpdateVersion) {
        console.warn(
          `[RoleCapacitiesManager] ⚠️ version obsoleta tras getArmadorEntry: version=${version} actual=${this.armadorUpdateVersion}, descartando`,
        )
        return
      }
      this.userArmadorIsJuridica = armadorEntry?.tipo_persona === 'juridica'
      this.roleDetails = {
        role: UserRoles.EXTERNO,
        externalSpec: ExternalSpec.ARMADOR,
        armadorType: ArmadorType.ESTE,
        armadorIsJuridica: this.userArmadorIsJuridica || undefined,
        isRepresentante: true,
      }
      this.refUserRole.value = this.roleDetails
      this.notifySubscribers()
      await this.findArmadorMuelleById(armadorId, version)
    } else {
      // Reverted to personal account — re-detect original spec
      console.log(`[RoleCapacitiesManager] volviendo a cuenta personal, llamando updateLoggedUser`)
      await this.updateLoggedUser(this.loggedUser)
    }
  }

  private async findArmadorMuelleById(armadorId: number, version: number) {
    console.log(`[RoleCapacitiesManager] findArmadorMuelleById armadorId=${armadorId} version=${version}`)
    const asociacionesRequests = await dataStorageManager.getAndFilterDataInBucket<IRequest>(
      BucketDefaultIDs.SERVICES,
      (r: IRequest) =>
        r.tipo_objeto === RequestObjectType.Asociacion_embarcacion && r.estado === 1 && r.creador === armadorId,
    )
    if (version !== this.armadorUpdateVersion) {
      console.warn(
        `[RoleCapacitiesManager] ⚠️ version obsoleta tras getAsociaciones: version=${version} actual=${this.armadorUpdateVersion}, descartando`,
      )
      return
    }
    const embarcacionesAsociadas = await dataStorageManager.getAndFilterDataInBucket<IEmbarcacion>(
      BucketDefaultIDs.EMBARCACIONES,
      (embarcacion) => asociacionesRequests.some((req) => req.id_objeto === embarcacion.id.toString()),
    )
    if (version !== this.armadorUpdateVersion) {
      console.warn(
        `[RoleCapacitiesManager] ⚠️ version obsoleta tras getEmbarcaciones: version=${version} actual=${this.armadorUpdateVersion}, descartando`,
      )
      return
    }
    const tieneMuelleWeste = embarcacionesAsociadas.some(
      (e) => e.tipo_embarcacion === 'fletera' || e.tipo_embarcacion === 'pilotaje' || e.tipo_embarcacion === 'auxiliar',
    )
    this.userArmadorType = tieneMuelleWeste ? ArmadorType.WESTE : ArmadorType.ESTE
    this.roleDetails = {
      role: UserRoles.EXTERNO,
      externalSpec: ExternalSpec.ARMADOR,
      armadorType: this.userArmadorType,
      armadorIsJuridica: this.userArmadorIsJuridica || undefined,
      isRepresentante: true,
    }
    console.log(
      `[RoleCapacitiesManager] ✅ findArmadorMuelleById completado. armadorType=${this.userArmadorType}, isRepresentante=true`,
    )
    this.refUserRole.value = this.roleDetails
    this.notifySubscribers()
  }

  /**
   * True si el usuario logueado actúa como Representante Legal del armador activo
   * (o de cualquier armador si no hay seleccionado). Legal otorga capacidades del
   * armador completo (espejado), sin matriz IPermisoControl.
   *
   * Usar en UI para mostrar indicadores diferenciadores entre Apoderado y Legal
   * (chip "Legal" en header, etiqueta en preferences, etc). La lógica de permisos
   * ya está cubierta automáticamente por resolveRepresentanteAccess.
   */
  public isActingAsRepresentanteLegal(): boolean {
    if (!this.loggedUser) return false
    const isActingAsRepresentante =
      this.loggedUser.rol === UserRoles.REPRESENTANTE || this.roleDetails?.isRepresentante === true
    if (!isActingAsRepresentante) return false

    const solicitudesList = dataStorageManager.getCurrentBucketData<IRequest>(BucketDefaultIDs.SOLICITUDES) || []
    const activeArmadorId = identityManager.getActiveArmadorId()
    return solicitudesList.some(
      (r) =>
        (r.tipo_objeto === RequestObjectType.RepresentanteArmador ||
          r.tipo_objeto === RequestObjectType.RepresentanteProveedor) &&
        r.id_objeto_2 === this.loggedUser!.id_usuario.toString() &&
        r.estado === RequestStatus.APPROVED_LEGAL &&
        new Date(r.fecha_caducidad) > new Date() &&
        (activeArmadorId === null || r.id_objeto === activeArmadorId.toString()),
    )
  }

  /**
   * True si el usuario opera como representante APODERADO (es decir, representante NO legal).
   * El apoderado administra embarcaciones vía la matriz IPermisoControl pero NO posee las
   * capacidades plenas del armador: en particular, NO puede desasociar embarcaciones de un
   * armador (bug #10 01-06 — esa acción queda restringida a admin / armador / representante legal).
   */
  public isActingAsApoderado(): boolean {
    const actuandoComoRep =
      this.loggedUser?.rol === UserRoles.REPRESENTANTE || this.roleDetails?.isRepresentante === true
    return actuandoComoRep && !this.isActingAsRepresentanteLegal()
  }

  /**
   * Resuelve si el representante actualmente logueado tiene acceso a un módulo y acción dados.
   * Si el usuario no es representante, devuelve true (sin restricción).
   *
   * @param modulo      SystemModules o string con jerarquía (ej: "2;15")
   * @param action      ActionsPermissions a verificar
   * @param embarcacionId  Opcional — si se provee, evalúa acceso a embarcación específica
   */
  public resolveRepresentanteAccess(
    modulo: SystemModules | string,
    action: ActionsPermissions = ActionsPermissions.VER,
    embarcacionId?: number,
  ): boolean {
    if (!this.loggedUser) return false

    // Solo aplica a representantes puros (rol 6) O usuarios operando como representantes de otro armador
    // Para roles internos y externos no-representantes → acceso libre
    const isActingAsRepresentante =
      this.loggedUser.rol === UserRoles.REPRESENTANTE || this.roleDetails?.isRepresentante === true
    if (!isActingAsRepresentante) return true

    const solicitudesList = dataStorageManager.getCurrentBucketData<IRequest>(BucketDefaultIDs.SOLICITUDES) || []
    const permisosList = dataStorageManager.getCurrentBucketData<IPermisoControl>(BucketDefaultIDs.PERMISOS) || []

    // Obtener solicitudes de representación activas del usuario, filtradas por el armador activo si corresponde.
    // Si alguna está APPROVED_LEGAL gana Legal (acceso espejado al armador, sin matriz de permisos).
    const activeArmadorId = identityManager.getActiveArmadorId()
    const solicitudesActivas = solicitudesList.filter(
      (r) =>
        (r.tipo_objeto === RequestObjectType.RepresentanteArmador ||
          r.tipo_objeto === RequestObjectType.RepresentanteProveedor) &&
        r.id_objeto_2 === this.loggedUser!.id_usuario.toString() &&
        isApproved(r.estado) &&
        new Date(r.fecha_caducidad) > new Date() &&
        (activeArmadorId === null || r.id_objeto === activeArmadorId.toString()),
    )

    if (solicitudesActivas.length === 0) return false

    // Representante Legal: capacidades del armador (espejado). No consulta matriz IPermisoControl.
    const tieneLegal = solicitudesActivas.some((r) => r.estado === RequestStatus.APPROVED_LEGAL)
    if (tieneLegal) return true

    // Apoderado clásico: aplica matriz IPermisoControl. Se toma la primera APPROVED.
    const solicitudActiva = solicitudesActivas[0]
    const permisosRep = permisosList.filter((p) => Number(p.representante) === Number(solicitudActiva.id))

    if (embarcacionId !== undefined) {
      // Calcular otros reps activos para el mismo armador
      const otrosReps = solicitudesList.filter(
        (r) =>
          r.tipo_objeto === solicitudActiva.tipo_objeto &&
          r.id_objeto === solicitudActiva.id_objeto &&
          r.id !== solicitudActiva.id &&
          isApproved(r.estado) &&
          new Date(r.fecha_caducidad) > new Date(),
      ).length
      return resolveEmbarcacionAccess(permisosRep, embarcacionId, otrosReps)
    }

    // La dimensión de menús/módulos se retiró: permiso_control solo controla
    // embarcaciones. Para una consulta que no es de embarcación no hay restricción.
    return true
  }

  private async findArmadorMuelle() {
    if (this.userExternalSpec === ExternalSpec.ARMADOR && this.loggedUser) {
      const userRutNorm = normalizeRut(this.loggedUser.rut)
      const searchedArmador = await dataStorageManager.getAndSearchDataInBucket<IArmador>(
        BucketDefaultIDs.ARMADORES,
        (armador: IArmador) =>
          normalizeRut(armador.rut_representante) === userRutNorm ||
          (armador.rut_empresa ? normalizeRut(armador.rut_empresa) === userRutNorm : false),
      )
      if (searchedArmador) {
        const asociacionesRequests = await dataStorageManager.getAndFilterDataInBucket<IRequest>(
          BucketDefaultIDs.SERVICES,
          (r: IRequest) =>
            r.tipo_objeto === RequestObjectType.Asociacion_embarcacion &&
            r.estado === 1 &&
            r.creador === searchedArmador!.id,
        )
        const embarcacionesAsociadas = await dataStorageManager.getAndFilterDataInBucket<IEmbarcacion>(
          BucketDefaultIDs.EMBARCACIONES,
          (embarcacion) => {
            return asociacionesRequests.some((req) => req.id_objeto === embarcacion.id.toString())
          },
        )
        const tieneMuelleWeste = embarcacionesAsociadas.some((embarcacion) => {
          return (
            embarcacion.tipo_embarcacion === 'fletera' ||
            embarcacion.tipo_embarcacion === 'pilotaje' ||
            embarcacion.tipo_embarcacion === 'auxiliar'
          )
        })
        this.userArmadorType = tieneMuelleWeste ? ArmadorType.WESTE : ArmadorType.ESTE
        this.roleDetails = {
          role: this.userRole!,
          externalSpec: this.userExternalSpec!,
          armadorType: this.userArmadorType,
          armadorIsJuridica: this.userArmadorIsJuridica || undefined,
        }
        this.refUserRole.value = this.roleDetails
        this.notifySubscribers()
      }
    }
  }
}

const roleCapacitiesManager = new RoleCapacitiesManager()
export default roleCapacitiesManager

export function getRoleName(role: UserRoles, spec?: ExternalSpec): string {
  switch (role) {
    case UserRoles.ADMIN:
      return 'Administrador'
    case UserRoles.SUPERVISOR:
      return 'Supervisor'
    case UserRoles.OPERADOR:
      return 'Operador'
    case UserRoles.GESTOR:
      return 'Gestor'
    case UserRoles.REPRESENTANTE:
      return 'Apoderado'
    case UserRoles.INTERINO:
      return 'Otros'
    case UserRoles.ESPORADICO:
      return 'Esporádico'
    case UserRoles.GENERICO:
      return 'Genérico'
    case UserRoles.EXTERNO:
      switch (spec) {
        case ExternalSpec.ARMADOR:
          return 'Armador'
        case ExternalSpec.PROVEEDOR:
          return 'Proveedor'
        case ExternalSpec.TRIPULANTE:
          return 'Tripulante'
        default:
          return 'Externo'
      }
    default:
      return 'Desconocido'
  }
}
